import * as THREE from 'three';

/**
 * Per-avatar finger tuning — constant additive rotations on the TARGET rig's
 * finger joints, applied right after the retargeter each frame (and during GLB
 * baking). This is the app-side answer to the classic VRM 0.x thumb problem:
 * mocap skeletons rest thumbs 45° out with the nail rolled sideways (the
 * Mecanim / VRM 1.0 T-stance spec), while VRoid/VRM 0.x avatars rest thumbs
 * naturally alongside the fingers — so world-delta retargeting reproduces the
 * convention mismatch as a constant offset. See docs/VRM_THUMBS.md.
 *
 * Angles are defined in each HAND's own frame (derived from the target bind
 * geometry: finger direction, thumb direction, palm normal), so one set of
 * slider values mirrors correctly across both hands and any rig orientation.
 */

export interface FingerTune {
  /** UI memory: the VRM 0.x preset toggle state. */
  vrm0Fix: boolean;
  /** Thumb abduction away from the fingers (degrees; the VRM 0.x culprit). */
  thumbOut: number;
  /** Roll around the thumb's own axis (degrees; nail orientation). */
  thumbRoll: number;
  /** Extra thumb curl (degrees, applied to the two outer joints). */
  thumbCurl: number;
  /** Curl added to all four fingers' joints (degrees). */
  fingerCurl: number;
  /** Spread of the four fingers around the palm normal (degrees, ±fanned). */
  fingerSplay: number;
  /**
   * Hand-tracker repair, per side (only one spider tracker flips at a time):
   * `flip` rotates the whole hand around the forearm axis (the "impossible
   * 180° hand"), `turn` rotates it around the palm normal (the "hand yawed
   * 90° sideways parallel to the palm"). Degrees.
   */
  handFlipLeft: number;
  handFlipRight: number;
  handTurnLeft: number;
  handTurnRight: number;
}

/** The numeric slider fields (everything except the vrm0Fix toggle). */
export const FINGER_TUNE_FIELDS = [
  'thumbOut',
  'thumbRoll',
  'thumbCurl',
  'fingerCurl',
  'fingerSplay',
  'handFlipLeft',
  'handFlipRight',
  'handTurnLeft',
  'handTurnRight',
] as const;
export type FingerTuneField = (typeof FINGER_TUNE_FIELDS)[number];

export function defaultFingerTune(): FingerTune {
  return {
    vrm0Fix: false,
    thumbOut: 0,
    thumbRoll: 0,
    thumbCurl: 0,
    fingerCurl: 0,
    fingerSplay: 0,
    handFlipLeft: 0,
    handFlipRight: 0,
    handTurnLeft: 0,
    handTurnRight: 0,
  };
}

export function isFingerTuneActive(t: FingerTune): boolean {
  return FINGER_TUNE_FIELDS.some((f) => Math.abs(t[f]) > 0.01);
}

export function sanitizeFingerTune(raw: unknown): FingerTune {
  const t = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<FingerTune>;
  const num = (v: unknown): number => (typeof v === 'number' && isFinite(v) ? v : 0);
  const out = defaultFingerTune();
  out.vrm0Fix = t.vrm0Fix === true;
  for (const f of FINGER_TUNE_FIELDS) out[f] = num(t[f]);
  return out;
}

/** A keyframed finger tune: at `time` (seconds) the hands hold these values. */
export interface FingerTuneKey {
  time: number;
  tune: FingerTune;
}

/**
 * Evaluate finger-tune keys at time t INTO the live tune (mutating in place).
 * Smoothstep between neighbours, clamped outside the keyed range — so a hand
 * repair can blend in exactly when the tracker flips mid-take.
 */
export function evalFingerTuneKeys(keys: FingerTuneKey[], t: number, out: FingerTune): void {
  if (keys.length === 0) return;
  let k1 = keys[0];
  let k2 = keys[0];
  for (let i = 0; i < keys.length; i++) {
    if (keys[i].time <= t) {
      k1 = keys[i];
      k2 = keys[Math.min(i + 1, keys.length - 1)];
    } else {
      if (i === 0) k2 = keys[0];
      break;
    }
  }
  const span = k2.time - k1.time;
  const s = span > 1e-9 ? Math.min(Math.max((t - k1.time) / span, 0), 1) : 1;
  const eased = s * s * (3 - 2 * s);
  for (const f of FINGER_TUNE_FIELDS) {
    out[f] = k1.tune[f] + (k2.tune[f] - k1.tune[f]) * eased;
  }
  out.vrm0Fix = (eased < 0.5 ? k1 : k2).tune.vrm0Fix;
}

const FINGERS = ['index', 'middle', 'ring', 'pinky'] as const;
/** Splay weights: fingers fan away from the middle finger. */
const SPLAY_WEIGHT: Record<(typeof FINGERS)[number], number> = {
  index: 1,
  middle: 0.25,
  ring: -0.4,
  pinky: -1,
};

interface HandRig {
  side: 'left' | 'right';
  hand: THREE.Object3D;
  /** Forearm bone (the hand-flip rotation axis runs forearm → hand). */
  forearm: THREE.Object3D | null;
  handBindWorldInv: THREE.Quaternion;
  /** Axes in HAND-bind space (rotated by the live hand each frame). */
  palmAxis: THREE.Vector3; // hand-turn (per-side) axis; palm normal, consistent world dir
  splayAxis: THREE.Vector3; // splay / thumb-abduction axis (raw palm normal, mirrors per hand)
  curlAxis: THREE.Vector3; // finger bend axis
  thumbRollAxis: THREE.Vector3; // thumb roll axis (along the thumb, mirrored per hand)
  thumbCurlAxis: THREE.Vector3; // thumb bend axis
  thumb: THREE.Object3D[]; // joints 1..3 (present ones)
  fingers: { joints: THREE.Object3D[]; splayWeight: number }[];
}

export interface FingerTuner {
  /** Apply the tune to the target joints (call right after retargeter.apply). */
  apply: (tune: FingerTune) => void;
  /** True when the rig had enough finger bones to tune. */
  hasFingers: boolean;
}

const DEG2RAD = Math.PI / 180;
const rot = new THREE.Quaternion();
const worldQ = new THREE.Quaternion();
const parentQ = new THREE.Quaternion();
const handNow = new THREE.Quaternion();
const handDelta = new THREE.Quaternion();
const axisNow = new THREE.Vector3();

/** Premultiply a world-space axis/angle rotation onto a bone. */
function rotateWorld(bone: THREE.Object3D, axis: THREE.Vector3, degrees: number): void {
  if (Math.abs(degrees) < 0.01) return;
  rot.setFromAxisAngle(axis, degrees * DEG2RAD);
  bone.getWorldQuaternion(worldQ);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(parentQ);
    bone.quaternion.copy(parentQ.invert()).multiply(rot.multiply(worldQ));
  } else {
    bone.quaternion.copy(rot.multiply(worldQ));
  }
  bone.updateMatrixWorld(true);
}

/**
 * Build a tuner for one actor's TARGET rig. `boneFor` resolves canonical keys
 * ('lefthand', 'lefthandthumb1', …) to target bones — pass the retarget pair
 * lookup. Must be called while the target is still in its bind pose (i.e.
 * right after createRetargeter) so the hand axes derive from rest geometry.
 */
export function createFingerTuner(
  boneFor: (canonical: string) => THREE.Object3D | null
): FingerTuner {
  const hands: HandRig[] = [];

  for (const side of ['left', 'right'] as const) {
    const hand = boneFor(`${side}hand`);
    if (!hand) continue;
    const thumb = [1, 2, 3]
      .map((j) => boneFor(`${side}handthumb${j}`))
      .filter((b): b is THREE.Object3D => !!b);
    const fingers = FINGERS.map((finger) => ({
      joints: [1, 2, 3]
        .map((j) => boneFor(`${side}hand${finger}${j}`))
        .filter((b): b is THREE.Object3D => !!b),
      splayWeight: SPLAY_WEIGHT[finger],
    })).filter((f) => f.joints.length > 0);
    if (thumb.length === 0 && fingers.length === 0) continue;

    // Bind-pose geometry → hand axes.
    hand.updateWorldMatrix(true, true);
    const handPos = hand.getWorldPosition(new THREE.Vector3());
    const middleRef =
      boneFor(`${side}handmiddle1`) ?? boneFor(`${side}handindex1`) ?? fingers[0]?.joints[0];
    const fingerDir = middleRef
      ? middleRef.getWorldPosition(new THREE.Vector3()).sub(handPos).normalize()
      : new THREE.Vector3(side === 'left' ? -1 : 1, 0, 0);
    let thumbDir: THREE.Vector3;
    if (thumb.length >= 2) {
      thumbDir = thumb[1]
        .getWorldPosition(new THREE.Vector3())
        .sub(thumb[0].getWorldPosition(new THREE.Vector3()))
        .normalize();
    } else if (thumb.length === 1) {
      thumbDir = thumb[0].getWorldPosition(new THREE.Vector3()).sub(handPos).normalize();
    } else {
      thumbDir = new THREE.Vector3(0, 0, 1);
    }
    // Palm normal from finger × thumb. Because the thumb mirrors across hands,
    // the raw cross points OUT of the right palm but INTO the left one. There are
    // two distinct needs, and they want OPPOSITE sign conventions:
    //
    //  • Axes built via a cross with fingerDir/thumbDir (curl, thumbCurl) want the
    //    NEGATED-on-left "consistent" palm normal, so the cross flips correctly and
    //    both hands curl toward their own palm. (This was the original curl fix.)
    //  • Rotations taken DIRECTLY around the palm normal or thumb direction (splay,
    //    thumbOut, thumbRoll) want a MIRROR-consistent axis — one that flips sign
    //    between hands. Using the "consistent" (same world dir) axis there makes a
    //    positive slider fan the right hand one way and the left the opposite way.
    const rawPalmAxis = new THREE.Vector3().crossVectors(fingerDir, thumbDir).normalize();
    if (rawPalmAxis.lengthSq() < 1e-6) rawPalmAxis.set(0, 1, 0);
    const palmAxis = rawPalmAxis.clone();
    if (side === 'left') palmAxis.negate();
    const curlAxis = new THREE.Vector3().crossVectors(palmAxis, fingerDir).normalize();
    const thumbCurlAxis = new THREE.Vector3().crossVectors(palmAxis, thumbDir).normalize();
    // Splay / thumb-abduction spin the finger IN the palm plane, around the palm
    // normal itself — use the raw (mirror-consistent) normal, not the consistent one.
    const splayAxis = rawPalmAxis.clone();
    // Thumb roll spins around the thumb's own direction; that direction is a polar
    // vector pointing the same way on both hands, so as a rotation axis it must be
    // negated on the left to mirror.
    const thumbRollAxis = thumbDir.clone();
    if (side === 'left') thumbRollAxis.negate();

    const handBindWorldInv = hand.getWorldQuaternion(new THREE.Quaternion()).invert();
    // Store axes in hand-bind space so they follow the animated hand.
    for (const axis of [palmAxis, splayAxis, curlAxis, thumbRollAxis, thumbCurlAxis]) {
      axis.applyQuaternion(handBindWorldInv);
    }

    hands.push({
      side,
      hand,
      forearm: boneFor(`${side}forearm`),
      handBindWorldInv,
      palmAxis,
      splayAxis,
      curlAxis,
      thumbRollAxis,
      thumbCurlAxis,
      thumb,
      fingers,
    });
  }

  function axisInWorld(axis: THREE.Vector3): THREE.Vector3 {
    return axisNow.copy(axis).applyQuaternion(handDelta);
  }

  const flipAxis = new THREE.Vector3();
  const forearmPos = new THREE.Vector3();
  const handPos = new THREE.Vector3();

  function apply(tune: FingerTune): void {
    if (!isFingerTuneActive(tune)) return;
    for (const rig of hands) {
      // Hand-tracker repair FIRST (the finger axes ride on the repaired hand).
      const flip = rig.side === 'left' ? tune.handFlipLeft : tune.handFlipRight;
      const turn = rig.side === 'left' ? tune.handTurnLeft : tune.handTurnRight;
      if (Math.abs(flip) > 0.01 && rig.forearm) {
        // Axis along the forearm → hand (live positions; unaffected by the flip).
        rig.forearm.updateWorldMatrix(true, false);
        forearmPos.setFromMatrixPosition(rig.forearm.matrixWorld);
        rig.hand.updateWorldMatrix(false, false);
        handPos.setFromMatrixPosition(rig.hand.matrixWorld);
        flipAxis.subVectors(handPos, forearmPos);
        if (flipAxis.lengthSq() > 1e-8) {
          rotateWorld(rig.hand, flipAxis.normalize(), flip);
        }
      }
      if (Math.abs(turn) > 0.01) {
        // Palm normal is invariant under its own rotation — order-safe.
        rig.hand.getWorldQuaternion(handNow);
        handDelta.copy(handNow);
        rotateWorld(rig.hand, axisInWorld(rig.palmAxis), turn);
      }

      rig.hand.getWorldQuaternion(handNow);
      handDelta.copy(handNow); // hand-bind space → current world space
      // (axes were stored premultiplied by handBindWorldInv, so applying the
      // live hand rotation yields the axis in world space now)

      // Thumb: abduction + roll at the base, curl on the outer joints.
      if (rig.thumb[0]) {
        rotateWorld(rig.thumb[0], axisInWorld(rig.splayAxis), tune.thumbOut);
        rotateWorld(rig.thumb[0], axisInWorld(rig.thumbRollAxis), tune.thumbRoll);
      }
      for (let j = 1; j < rig.thumb.length; j++) {
        rotateWorld(rig.thumb[j], axisInWorld(rig.thumbCurlAxis), tune.thumbCurl);
      }

      // Fingers: curl on every joint, splay fanned from the middle finger.
      for (const finger of rig.fingers) {
        if (finger.joints[0] && Math.abs(tune.fingerSplay) > 0.01) {
          rotateWorld(
            finger.joints[0],
            axisInWorld(rig.splayAxis),
            tune.fingerSplay * finger.splayWeight
          );
        }
        if (Math.abs(tune.fingerCurl) > 0.01) {
          for (const joint of finger.joints) {
            rotateWorld(joint, axisInWorld(rig.curlAxis), tune.fingerCurl);
          }
        }
      }
    }
  }

  return { apply, hasFingers: hands.length > 0 };
}
